import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { MUTED_TEXT } from '../colors';

export type EmptyStateStep = {
  title: string;
  description: string;
};

type Props = {
  /** The italic one-liner. Quotation marks belong in the string. */
  quote: string;
  body: string;
  /** Omitted where the design sets hasAction=false, as on the archive tab. */
  action?: React.ReactNode;
  /**
   * The "Jak to funguje" strip. Deliberately only on the first tab: the DevNote
   * on the design says Klara is an unknown feature there and a CTA alone does
   * not explain it, while the other tabs address someone who already knows.
   */
  steps?: EmptyStateStep[];
};

/**
 * The empty state of a Klara list, after DS Empty Study Space: an italic line
 * in quotes, one factual sentence, and at most one green action.
 *
 * Greys come from ../colors: the --color-black40 family the rest of Klara
 * reached for is declared nowhere.
 */
export function EmptyState({ quote, body, action, steps }: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '64px 24px',
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontStyle: 'italic',
          color: MUTED_TEXT,
          textAlign: 'center',
          maxWidth: '660px',
        }}
      >
        {quote}
      </Typography>

      <Typography
        sx={{
          fontSize: '15px',
          color: 'var(--color-black-text)',
          textAlign: 'center',
          maxWidth: '660px',
          marginTop: '12px',
        }}
      >
        {body}
      </Typography>

      {action && <Box sx={{ marginTop: '24px' }}>{action}</Box>}

      {steps && steps.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: '56px',
          }}
        >
          {steps.map((step, index) => (
            <Box
              key={step.title}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                width: '300px',
                padding: '0 24px',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '12px',
                  // The primary tints follow the company space's own colour,
                  // the way the rest of the app does.
                  backgroundColor: 'var(--color-primary15)',
                  color: 'var(--color-primary60)',
                  fontSize: '12px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {index + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: 'var(--color-black)',
                  textAlign: 'center',
                }}
              >
                {step.title}
              </Typography>
              <Typography
                sx={{ fontSize: '12px', color: MUTED_TEXT, textAlign: 'center' }}
              >
                {step.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
